import React from 'react';
import { ListItemText, Grid, Card, CardHeader, CardContent, CardActions, List, ListItem, Button, Box, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Menu, MenuItem } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useDirectBankingMiddlewareApi } from '../../context';
import utils from '../../services/utils.service';
import moment from 'moment';
import { DirectBankingOrderDetails } from '../../services/types/directBanking/OrderDetails.type';
import { LoadingButton } from '../../components/loadingButton';
import { xml2json } from 'xml-js';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Attachments } from '../../components/attachments';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardActions: {
      justifyContent: 'flex-end'
    },
    link: {
      color: 'grey',
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      marginRight: theme.spacing(0.9),
      fontSize: '1.1rem',
    },
  })
);

const RefundModal = (props: { orderId?: number, open: boolean, onClose: () => void }) => {
  const { open, onClose, orderId } = props;
  const api = useDirectBankingMiddlewareApi();
  const { t } = useTranslation('directBankingOrders');
  const [loading, setLoading] = React.useState(false);
  const [amountToRefund, setAmountToRefund] = React.useState(0);

  const [errors, setErrors] = React.useState({
    inputAmount: false,
    insufficientAmount: false
  });
  const resetErrors = () => {
    setErrors({
      inputAmount: false,
      insufficientAmount: false
    });
  }

  React.useEffect(() => {
    setLoading(false);
  }, [open]);

  const issueRefund = () => {
    if (!orderId) {
      return;
    }
    utils.runAsync(async () => {
      setErrors({ inputAmount: false, insufficientAmount: false });
      if (amountToRefund <= 0 || !amountToRefund) {
        setErrors({ inputAmount: true, insufficientAmount: false });
        return;
      }
      setLoading(true);
      try {
        await api.issueDirectBankingRefund(orderId, amountToRefund);
        onClose();
      } catch (e) {
        setErrors({ inputAmount: false, insufficientAmount: true })
      }
    }, (e) => {
      setLoading(false);
    })
  }

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{t('REFUND_MODAL_TITLE')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('REFUND_MODAL_DESCRIPTION')}
        </DialogContentText>
        <TextField
          label={t('AMOUNT')}
          value={amountToRefund}
          type="number"
          inputProps={{
            step: "0.01"
          }}
          error={errors.inputAmount || errors.insufficientAmount}
          helperText={errors.inputAmount ? t('REQUIRED') : (errors.insufficientAmount ? t('INSUFFICIENT_AMOUNT_TO_REFUND') : '')}
          onChange={(e) => { setAmountToRefund(parseFloat(e.target.value)); resetErrors(); }}
          fullWidth
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('CANCEL')}
        </Button>
        <LoadingButton color='primary' loading={loading} disabled={loading} onClick={issueRefund}>
          {t('ISSUE_REFUND_BUTTON_TITLE')}
        </LoadingButton>
      </DialogActions>
    </Dialog >
  )
}

const ExceptionModal = (props: { orderId?: number, open: boolean, onClose: () => void }) => {
  const { open, onClose, orderId } = props;
  const api = useDirectBankingMiddlewareApi();
  const { t } = useTranslation('directBankingOrders');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(false);
  }, [open]);

  const markAsException = () => {
    if (!orderId) {
      return;
    }
    utils.runAsync(async () => {
      setLoading(true);
      try {
        await api.changeOrderExceptionStatus(orderId, 'EXCEPTION');
        onClose();
      } catch (e) {
      }
    }, (e) => {
      setLoading(false);
    })
  }

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{t('EXCEPTION_MODAL_TITLE')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('EXCEPTION_MODAL_DESCRIPTION')}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('CANCEL')}
        </Button>
        <LoadingButton color='primary' loading={loading} disabled={loading} onClick={markAsException}>
          {t('MARK_EXCEPTION_BUTTON_TITLE')}
        </LoadingButton>
      </DialogActions>
    </Dialog >
  )
}

const ExceptionModalRefund = (props: { orderId?: number, open: boolean, onClose: () => void }) => {
  const { open, onClose, orderId } = props;
  const api = useDirectBankingMiddlewareApi();
  const { t } = useTranslation('directBankingOrders');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(false);
  }, [open]);

  const markAsExceptionRefund = () => {
    if (!orderId) {
      return;
    }
    utils.runAsync(async () => {
      setLoading(true);
      try {
        await api.changeOrderExceptionStatus(orderId, 'EXCEPTION_REFUNDED');
        onClose();
      } catch (e) {
      }
    }, (e) => {
      setLoading(false);
    })
  }

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{t('EXCEPTION_REFUND_MODAL_TITLE')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('EXCEPTION_REFUND_MODAL_DESCRIPTION')}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('CANCEL')}
        </Button>
        <LoadingButton color='primary' loading={loading} disabled={loading} onClick={markAsExceptionRefund}>
          {t('MARK_EXCEPTION_REFUND_BUTTON_TITLE')}
        </LoadingButton>
      </DialogActions>
    </Dialog >
  )
}

const ResponseDetailsModal = (props: { response?: string, open: boolean, onClose: () => void }) => {
  const { response, open, onClose } = props;
  const { t } = useTranslation('revolusendTransfers');
  let responseString = response ? response : '';

  if (response) {
    try {
      responseString = JSON.stringify(JSON.parse(response), undefined, 2);
    } catch (e) {
      console.log('Could not parse JSON response, trying as XML.');
      try {
        responseString = JSON.stringify(JSON.parse(xml2json(response)), undefined, 2);
      } catch (e) {
        console.log('Could not decode xml response.')
      }
    }
  }
  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{t('RESPONSE_MODAL_TITLE')}</DialogTitle>
      <DialogContent>
        <pre>{responseString}</pre>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('OK')}
        </Button>
      </DialogActions>
    </Dialog >
  )
}

const ClearingModal = (props: { orderId?: number, open: boolean, onClose: () => void }) => {
  const { open, onClose, orderId: orderId } = props;
  const api = useDirectBankingMiddlewareApi();
  const { t } = useTranslation('directBankingOrders');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const proxyOnClose = () => {
    setError(undefined);
    onClose();
  }

  React.useEffect(() => {
    setLoading(false);
  }, [open]);

  const completeClearing = () => {
    setError(undefined);
    if (!orderId) {
      return;
    }
    utils.runAsync(async () => {
      setLoading(true);
      await api.completeClearing(orderId);
      proxyOnClose();
    }, (e) => {
      console.log(e);
      console.log(e.response);
      console.log(e.name);
      console.log(e.response?.status);
      console.log(e.message)
      if (e?.message == '405') {
        setError(t('CLEARING_ALREADY_VALID'));
      } else {
        setError(t('ERROR_IN_CLEARING'));
      }
      setLoading(false);
    })
  }

  return (
    <Dialog fullWidth open={open} onClose={proxyOnClose}>
      <DialogTitle>{t('CLEARING_MODAL_TITLE')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography gutterBottom>
            {t('CLEARING_MODAL_DESCRIPTION')}
          </Typography>
          {error &&
            <Typography color={'error'} variant='subtitle1'>
              {t(error)}
            </Typography>
          }
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={proxyOnClose}>
          {t('CANCEL')}
        </Button>
        <LoadingButton color='primary' loading={loading} disabled={loading} onClick={completeClearing}>
          {t('CONFIRM')}
        </LoadingButton>
      </DialogActions>
    </Dialog >
  )
}


export const OrdersDetails = (props: RouteComponentProps<{ id: string }>) => {
  const orderId = props.match.params.id;
  const { t } = useTranslation(['directBankingOrders']);
  const classes = useStyles();
  const api = useDirectBankingMiddlewareApi();
  const history = useHistory();

  const [orderDetails, setOrderDetails] = React.useState<DirectBankingOrderDetails | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [showRefundModal, setShowRefundModal] = React.useState(false);
  const [showExceptionModal, setShowExceptionModal] = React.useState(false);
  const [showResponseModal, setShowResponseModal] = React.useState(false);
  const [showExceptionRefundModal, setShowExceptionRefundModal] = React.useState(false);
  const [isFileSaved, setIsFileSaved] = React.useState(false);

  const [showCompleteClearingModal, setShowCompleteClearingModal] = React.useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };
  const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const loadTransactionDetails = () => {
    utils.runAsync(async () => {
      setLoading(true);
      setOrderDetails(await api.getDirectBankingOrderDetails(orderId));
    }, (e) => {
      setLoading(false);
      if (e) {
        console.log(e);
      }
    });
  };

  React.useEffect(() => {
    loadTransactionDetails();
  }, []);

  const handleFileSaved = () => {
    if (orderDetails?.order.status !== 'EXCEPTION_REFUNDED') {
      setIsFileSaved(true);
    }
  };

  React.useEffect(() => {
    if (isFileSaved) {
      setShowExceptionRefundModal(true);
      setIsFileSaved(false);
    }
  }, [isFileSaved]);

  if (loading && orderDetails === null) {
    return (
      <Grid container justify='center'>
        <Box marginTop={4}>
          <CircularProgress />
        </Box>
      </Grid>
    );
  }

  const getOrderStatusLabel = () => {
    if (orderDetails === null) return '';
    return orderDetails.order.status;
  };

  const getPaymentStatusLabel = () => {
    if (orderDetails === null || orderDetails.payment === null) return '';
    return orderDetails.payment.status;
  };

  const canRefund = () => {
    if (orderDetails?.order.paid === false) {
      return false;
    }
    if (['EXCEPTION', 'EXCEPTION_REFUNDED'].includes(orderDetails?.order.status ?? '')) {
      return false;
    }
    const amountRefunded = orderDetails?.refunds.reduce((acc, r) => acc + r.amount, 0) ?? 0;
    return amountRefunded < orderDetails?.order.amount!;
  };

  const validClearing = () => {
    if (orderDetails?.order.paid === false) {
      return undefined;
    }
    if (orderDetails?.consumer.location_id === undefined || orderDetails?.consumer.location_id === null) {
      return undefined;
    }
    let isValid = false;
    if (orderDetails?.order_clearing) {
      isValid = Boolean(
        (
          !orderDetails?.consumer.prefunded_fee &&
          orderDetails?.order_clearing.deposit_id
        ) ||
        (
          orderDetails?.consumer.prefunded_fee &&
          orderDetails?.order_clearing.deposit_id &&
          orderDetails?.order_clearing.fee_tx_id
        )
      );
    }
    return isValid!;
  };

  return (
    <>
      <RefundModal
        orderId={orderDetails?.order.id}
        open={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          loadTransactionDetails();
        }}
      />
      <ExceptionModal
        orderId={orderDetails?.order.id}
        open={showExceptionModal}
        onClose={() => {
          setShowExceptionModal(false);
          loadTransactionDetails();
        }}
      />
      <ExceptionModalRefund
        orderId={orderDetails?.order.id}
        open={showExceptionRefundModal}
        onClose={() => {
          setShowExceptionRefundModal(false);
          loadTransactionDetails();
        }}
      />

      <ResponseDetailsModal
        open={showResponseModal}
        response={orderDetails?.payment?.response}
        onClose={() => {
          setShowResponseModal(false);
        }}
      />

      <ClearingModal
        orderId={orderDetails?.order.id}
        open={showCompleteClearingModal}
        onClose={() => {
          setShowCompleteClearingModal(false);
          loadTransactionDetails();
        }} />


      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title={t('ORDER')} />
                <CardContent>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemText primary={t('ID')} secondary={orderDetails?.order.id} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('CONSUMER_EXTERNAL_REFERENCE')} secondary={orderDetails?.order.external_reference} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('CREATED')} secondary={orderDetails?.order.created ? moment(orderDetails.order.created).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('MODIFIED')} secondary={orderDetails?.order.modified ? moment(orderDetails.order.modified).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemText primary={t('AMOUNT')} secondary={`${utils.currencyFormat(orderDetails?.order.amount)} ${orderDetails?.order.currency_iso_code}`} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('STATUS')} secondary={t(getOrderStatusLabel())} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('PAID')} secondary={orderDetails?.order.paid ? t('YES') : t('NO')} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('HAS_REFUNDS')} secondary={orderDetails?.order.has_refunds ? t('YES') : t('NO')} />
                        </ListItem>
                      </List>
                    </Grid>
                    {orderDetails?.consumer.location_id &&
                      <>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                            {t('EXTERNAL_CONSUMER_DETAILS')}
                          </Typography>
                          <List dense disablePadding>
                            <ListItem disableGutters>
                              <ListItemText primary={t('VALID_CLEARING')} secondary={validClearing() === undefined ? t('NOT_APPLICABLE') : validClearing() ? t('YES') : t('NO')} />
                            </ListItem>
                            <ListItem disableGutters>
                              <ListItemText primary={t('CLEARING_DEPOSIT_ID')} secondary={orderDetails?.order_clearing?.deposit_id || '-'} />
                            </ListItem>
                            <ListItem disableGutters>
                              <ListItemText primary={t('CLEARING_DEPOSIT_DATE')} secondary={orderDetails?.order_clearing?.deposit_issued_at ? moment(orderDetails?.order_clearing?.deposit_issued_at).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                            </ListItem>
                          </List>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                            {'-------------------'}
                          </Typography>

                          <List dense disablePadding>
                            <ListItem disableGutters>
                              <ListItemText primary={t('CONSUMER_WITH_PREFUNDED_FEES')} secondary={orderDetails?.consumer.prefunded_fee ? t('YES') : t('NO')} />
                            </ListItem>
                            {orderDetails?.consumer.prefunded_fee && <>
                              <ListItem disableGutters>
                                <ListItemText primary={t('CLEARING_FEE_ID')} secondary={orderDetails?.order_clearing?.fee_tx_id || '-'} />
                              </ListItem>
                              <ListItem disableGutters>
                                <ListItemText primary={t('CLEARING_FEE_DATE')} secondary={orderDetails?.order_clearing?.fee_tx_issued_at ? moment(orderDetails?.order_clearing?.fee_tx_issued_at).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                              </ListItem>
                            </>}
                          </List>
                        </Grid>
                      </>
                    }
                  </Grid>
                </CardContent>
                <CardActions className={classes.cardActions}>
                  <Button variant='contained' color='primary' aria-controls='actions-menu' aria-haspopup='true' onClick={handleActionsClick}>
                    {t('ACTIONS')}
                  </Button>
                  <Menu id='actions-menu' anchorEl={actionsAnchorEl} keepMounted open={Boolean(actionsAnchorEl)} onClose={handleActionsClose}>
                    <MenuItem
                      disabled={orderDetails?.consumer.location_id === undefined || orderDetails?.consumer.location_id === null || !orderDetails.order.has_location_id}
                      onClick={() => {
                        setShowCompleteClearingModal(true);
                        handleActionsClose();
                      }}
                    >
                      {t('COMPLETE_CLEARING')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setShowExceptionModal(true);
                        handleActionsClose();
                      }}
                      disabled={orderDetails?.order.status === 'EXCEPTION' || orderDetails?.order.status === 'EXCEPTION_REFUNDED'}
                    >
                      {t('MARK_EXCEPTION_BUTTON_TITLE')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setShowExceptionRefundModal(true);
                        handleActionsClose();
                      }}
                      disabled={orderDetails?.order.status === 'EXCEPTION_REFUNDED'}
                    >
                      {t('MARK_EXCEPTION_REFUND_BUTTON_TITLE')}
                    </MenuItem>
                  </Menu>
                </CardActions>
              </Card>
            </Grid>
            {orderDetails && (
              <Grid item xs={12}>
                <Attachments
                  entity="directBankingOrder"
                  id={orderDetails.order.id.toString()}
                  onFileSaved={handleFileSaved}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title={t('PAYMENT')} />
            <CardContent>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText primary={t('CREATED')} secondary={orderDetails?.payment?.created ? moment(orderDetails.payment.created).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('STATUS')} secondary={t(getPaymentStatusLabel())} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('EXTERNAL_PAYMENT_REFERENCE')} secondary={orderDetails?.payment?.external_payment_reference} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('EXTERNAL_BANK_REFERENCE')} secondary={orderDetails?.payment?.external_bank_reference} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('ISSUER_NAME')} secondary={orderDetails?.payment?.issuer_name} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('ISSUER_BANK_ACCOUNT')} secondary={orderDetails?.payment?.issuer_bank_account} />
                </ListItem>
              </List>
            </CardContent>
            <CardActions className={classes.cardActions}>
              <LoadingButton
                onClick={() => setShowRefundModal(true)}
                disabled={!canRefund()}
                size='small'>
                {t('ISSUE_REFUND_BUTTON_TITLE')}
              </LoadingButton>
              <Button
                onClick={() => {
                  setShowResponseModal(true);
                }}
                disabled={!orderDetails?.payment || orderDetails?.payment?.response === null}>
                {t('VIEW_RESPONSE_BUTTON_TITLE')}
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <Grid container>
              <Grid item xs={12}>
                <Box paddingY={3} paddingX={2}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant='h5'>{t('REFUNDS')}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('AMOUNT')}</TableCell>
                        <TableCell>{t('STATUS')}</TableCell>
                        <TableCell>{t('DATE')}</TableCell>
                        <TableCell>{t('MODIFIED')}</TableCell>
                        <TableCell>{t('CLEARING_WITHDRAW_ID')}</TableCell>
                        <TableCell>{t('CLEARING_WITHDRAW_DATE')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(orderDetails?.refunds ?? [])
                        .sort((a, b) => moment(b.created).diff(moment(a.created)))
                        .map(r => (
                          <TableRow key={r.id}>
                            <TableCell>
                              {`${utils.currencyFormat(r.amount)} ${orderDetails?.order.currency_iso_code}`}
                            </TableCell>
                            <TableCell>
                              <Chip label={r.status} color={['PROCESSED'].includes(r.status) ? 'primary' : 'default'} />
                            </TableCell>
                            <TableCell>
                              {moment(r.created).format('DD-MM-YYYY HH:mm:ss')}
                            </TableCell>
                            <TableCell>
                              {r.modified ? moment(r.modified).format('DD-MM-YYYY HH:mm:ss') : '-'}
                            </TableCell>
                            <TableCell>
                              {r.clearing_withdraw_transaction_id}
                            </TableCell>
                            <TableCell>
                              {r.clearing_withdraw_issued_at ? moment(r.clearing_withdraw_issued_at).format('DD-MM-YYYY HH:mm:ss') : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};